/**
 * /downhole/reports — the three downhole reports (fieldiq-api#882
 * `GET /reports/:report`). `?tab=` picks one. Listed in the /reports catalog.
 *
 * TABS, NOT CHIPS: each switches which question the page answers (usage,
 * assemblies, work orders), so they are underlined and carry no count
 * (CLAUDE.md, tab strips). They sit in the body, under the band.
 *
 * THE RANGE IS THE API'S. Both days are tenant days; leave them blank and the
 * API answers the last 30 days. The footer prints the range the API actually
 * used (`meta.start_date` / `end_date`), not the inputs, so a blank picker
 * never reads as "all time".
 *
 * IN-RANGE AND LIFETIME ARE DIFFERENT FIGURES, labelled as such. A component's
 * in-range runs, hours and footage are the runs that went in during the range;
 * its lifetime counters are the stored totals. Wear is USED, by hours and by
 * runs, NULL (`—`) where there is no limit — the unrouted screen read a
 * `wear_percent` the API never sent and showed 0% in green for every tool.
 *
 * NO EXPORT. The old "Export CSV" was a coming-soon toast.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeReport } from '../../hooks/useDownhole';
import { formatCalendarDate, formatInstantDay, formatInstantTenantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtHours, fmtQty } from '../../lib/display-labels';
import {
  DH_ASSEMBLY_STATUS_SKIN,
  DH_CATEGORY_LABEL,
  DH_COMPONENT_STATUS_SKIN,
  DH_PRIORITY_LABEL,
  DH_WORK_ORDER_STATUS_SKIN,
  DH_WORK_TYPE_LABEL,
  dhLabel,
} from '../../lib/downhole-labels';
import { formatDh } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  DH_REPORTS,
  type DhAssemblyHistoryRow,
  type DhComponentUsageRow,
  type DhReport,
  type DhUnitSystem,
  type DhWorkOrder,
} from '../../types/downhole';
import { DH_DATE_INPUT, DH_SECONDARY, DhBadge, DhCategoryPill, DhEmpty, DhField, DhPanel, DhWearMeter } from './DownholeBits';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const TAB_LABEL: Record<DhReport, string> = {
  component_usage: 'Component usage',
  assembly_history: 'Assembly history',
  work_order_summary: 'Work orders',
};
const TH = 'whitespace-nowrap px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const TD = 'px-4 py-3 text-sm text-fieldiq-text';
const TD_NUM = 'whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text';

function ReportPanel<Row>({
  report,
  params,
  subject,
  render,
}: {
  report: DhReport;
  params: { start_date?: string; end_date?: string };
  subject: string;
  render: (rows: Row[], meta: { unit_system: DhUnitSystem | null; time_zone: string | null; currency: string | null }) => React.ReactNode;
}) {
  const query = useDownholeReport<Row>(report, params);
  const failure = queryLoadFailure(query);
  const rows = query.data?.data ?? [];
  const meta = query.data?.meta;
  const range =
    meta?.start_date && meta?.end_date
      ? `${formatCalendarDate(meta.start_date) ?? meta.start_date} – ${formatCalendarDate(meta.end_date) ?? meta.end_date}`
      : null;
  return (
    <DhPanel
      // Usage and assembly history count runs by their RUN-IN day; the work
      // order report counts work orders by the day they were OPENED.
      eyebrow={range ? `${report === 'work_order_summary' ? 'Opened' : 'Runs in'} ${range}` : 'Report'}
      title={TAB_LABEL[report]}
      testId={`downhole-report-${report}`}
      footer={!query.isLoading && !failure && range ? `${rows.length} ${rows.length === 1 ? 'row' : 'rows'} · ${range}` : undefined}
    >
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label={`Loading ${subject}`} />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject={subject} />
      ) : rows.length === 0 ? (
        <DhEmpty>Nothing in this range.</DhEmpty>
      ) : (
        <div className="overflow-x-auto">
          {render(rows, { unit_system: meta?.unit_system ?? null, time_zone: meta?.time_zone ?? null, currency: meta?.currency ?? null })}
        </div>
      )}
    </DhPanel>
  );
}

export default function ReportsScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: DhReport = (DH_REPORTS as readonly string[]).includes(tabParam ?? '') ? (tabParam as DhReport) : 'component_usage';
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const params = useMemo(
    () => ({ ...(DAY.test(start) && { start_date: start }), ...(DAY.test(end) && { end_date: end }) }),
    [start, end],
  );

  const setTab = (next: DhReport) => {
    const p = new URLSearchParams(searchParams);
    p.set('tab', next);
    setSearchParams(p, { replace: true });
  };

  return (
    <div data-testid="downhole-reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow="Downhole tools" title="Tool Reports" onBack={() => navigate(-1)} backLabel="Back" />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div role="tablist" aria-label="Report" className="scrollbar-hide flex gap-6 overflow-x-auto border-b border-fieldiq-border">
            {DH_REPORTS.map((r) => {
              const active = r === tab;
              return (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(r)}
                  // The active tab is marked by the amber underline and the
                  // weight, not by ink colour (CLAUDE.md: amber ink fails on
                  // the grey page in light mode).
                  className={`-mb-px whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                    active ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-semibold text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {TAB_LABEL[r]}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44">
              <DhField label="From">
                <input type="date" className={DH_DATE_INPUT} value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />
              </DhField>
            </div>
            <div className="w-44">
              <DhField label="To">
                <input type="date" className={DH_DATE_INPUT} value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
              </DhField>
            </div>
            {(start || end) && (
              <button type="button" className={DH_SECONDARY} onClick={() => { setStart(''); setEnd(''); }}>
                Last 30 days
              </button>
            )}
          </div>

          {tab === 'component_usage' && (
            <ReportPanel<DhComponentUsageRow>
              report="component_usage"
              params={params}
              subject="component usage"
              render={(rows, m) => (
                <table className="w-full min-w-[1240px]">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr>
                      <th scope="col" className={TH}>Component</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={`${TH} text-right`}>Runs · range</th>
                      <th scope="col" className={`${TH} text-right`}>Circ. hrs · range</th>
                      <th scope="col" className={`${TH} text-right`}>Footage · range</th>
                      <th scope="col" className={`${TH} text-right`}>Runs · lifetime</th>
                      <th scope="col" className={`${TH} text-right`}>Run hrs · lifetime</th>
                      <th scope="col" className={TH}>Wear used · hrs</th>
                      <th scope="col" className={TH}>Wear used · runs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.id} className="border-t border-fieldiq-border">
                        <td className={TD}>
                          <Link to={`/downhole/components/${c.id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                            {c.serial_number}
                          </Link>
                          <div className="mt-1"><DhCategoryPill category={c.category} /></div>
                        </td>
                        <td className={TD}><DhBadge map={DH_COMPONENT_STATUS_SKIN} value={c.status} /></td>
                        <td className={TD_NUM}>{fmtQty(c.runs_in_range)}</td>
                        <td className={TD_NUM}>{fmtHours(c.circulating_hours_in_range, 1)}</td>
                        <td className={TD_NUM}>{formatDh(c.footage_in_range, 'length', m.unit_system, { digits: 0 })}</td>
                        <td className={TD_NUM}>{fmtQty(c.lifetime_runs)}</td>
                        <td className={TD_NUM}>{fmtHours(c.lifetime_run_hours, 1)}</td>
                        <td className={TD}><DhWearMeter percent={c.wear_percent_hours} label="Wear used, of the hours limit" /></td>
                        <td className={TD}><DhWearMeter percent={c.wear_percent_runs} label="Wear used, of the runs limit" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            />
          )}

          {tab === 'assembly_history' && (
            <ReportPanel<DhAssemblyHistoryRow>
              report="assembly_history"
              params={params}
              subject="assembly history"
              render={(rows, m) => (
                <table className="w-full min-w-[1240px]">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr>
                      <th scope="col" className={TH}>Assembly</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={TH}>Customer</th>
                      <th scope="col" className={TH}>Well / Rig</th>
                      <th scope="col" className={`${TH} text-right`}>Runs</th>
                      <th scope="col" className={TH}>First run in</th>
                      <th scope="col" className={TH}>Last run out</th>
                      <th scope="col" className={`${TH} text-right`}>Footage</th>
                      <th scope="col" className={`${TH} text-right`}>Circ. hrs</th>
                      <th scope="col" className={TH}>Laid down</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.id} className="border-t border-fieldiq-border">
                        <td className={TD}>
                          <Link to={`/downhole/assemblies/${a.id}/build`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                            {a.assembly_number}
                          </Link>
                          <p className="text-xs text-fieldiq-muted">{a.name}</p>
                        </td>
                        <td className={TD}>
                          <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.status} />
                          {a.any_lost_in_hole && (
                            <span className="ml-2 rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                              Lost in hole
                            </span>
                          )}
                        </td>
                        <td className={`${TD} min-w-[12rem]`}>{displayValue(a.customer_name)}</td>
                        <td className={`${TD} min-w-[14rem]`}>{a.well_name || a.rig_name ? `${a.well_name || '—'} / ${a.rig_name || '—'}` : '—'}</td>
                        <td className={TD_NUM}>{fmtQty(a.run_count)}</td>
                        <td className={`${TD} whitespace-nowrap`}>{formatInstantDay(a.first_run_in_at, m.time_zone) ?? '—'}</td>
                        <td className={`${TD} whitespace-nowrap`}>{formatInstantDay(a.last_run_out_at, m.time_zone) ?? '—'}</td>
                        <td className={TD_NUM}>{formatDh(a.footage_drilled, 'length', m.unit_system, { digits: 0 })}</td>
                        <td className={TD_NUM}>{fmtHours(a.circulating_hours, 1)}</td>
                        <td className={`${TD} whitespace-nowrap`}>{formatInstantDay(a.laid_down_at, m.time_zone) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            />
          )}

          {tab === 'work_order_summary' && (
            <ReportPanel<DhWorkOrder>
              report="work_order_summary"
              params={params}
              subject="work orders"
              render={(rows, m) => (
                <table className="w-full min-w-[1180px]">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr>
                      <th scope="col" className={TH}>WO #</th>
                      <th scope="col" className={TH}>Component</th>
                      <th scope="col" className={TH}>Type</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={TH}>Priority</th>
                      <th scope="col" className={TH}>Assigned to</th>
                      <th scope="col" className={`${TH} text-right`}>Labour hrs</th>
                      <th scope="col" className={`${TH} text-right`}>Parts</th>
                      <th scope="col" className={`${TH} text-right`}>Total</th>
                      <th scope="col" className={TH}>Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((w) => (
                      <tr key={w.id} className="border-t border-fieldiq-border">
                        <td className={`${TD} whitespace-nowrap`}>
                          <Link to={`/downhole/work-orders/${w.id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                            {w.wo_number || '—'}
                          </Link>
                        </td>
                        <td className={TD}>
                          <p className="whitespace-nowrap font-mono text-white">{w.component_serial || '—'}</p>
                          <p className="text-xs text-fieldiq-muted">{dhLabel(DH_CATEGORY_LABEL, w.component_category)}</p>
                        </td>
                        <td className={TD}>{dhLabel(DH_WORK_TYPE_LABEL, w.work_type)}</td>
                        <td className={TD}><DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={w.status} /></td>
                        <td className={TD}>{dhLabel(DH_PRIORITY_LABEL, w.priority)}</td>
                        <td className={`${TD} whitespace-nowrap`}>{displayValue(w.assigned_to_name)}</td>
                        <td className={TD_NUM}>{fmtHours(w.labor_hours, 1)}</td>
                        <td className={TD_NUM}>{fmtCurrency(w.parts_cost, m.currency)}</td>
                        <td className={TD_NUM}>{fmtCurrency(w.total_cost, m.currency)}</td>
                        <td className={`${TD} whitespace-nowrap`}>{formatInstantTenantDay(w.created_at, m.time_zone) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            />
          )}
        </div>
      </section>
    </div>
  );
}
